const DEFAULT_FALLBACK = "Erreur inconnue";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Best-effort message from thrown values (Error, string, { message }). */
function messageFromUnknown(value: unknown): string | undefined {
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? value : undefined;
  }
  if (value instanceof Error) {
    const m = value.message?.trim();
    return m && m.length > 0 ? value.message : undefined;
  }
  if (isRecord(value)) {
    const msg = value.message;
    if (typeof msg === "string") {
      const t = msg.trim();
      return t.length > 0 ? msg : undefined;
    }
  }
  return undefined;
}

/**
 * Safe user-facing message from a catch binding. Use optional `fallback` when
 * the UI already had a domain-specific default.
 */
export function getErrorMessage(error: unknown, fallback: string = DEFAULT_FALLBACK): string {
  return messageFromUnknown(error) ?? fallback;
}

/**
 * Message from Supabase `functions.invoke` failures (`context.body` may hold edge payload).
 */
export function getEdgeFunctionErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== "object" || error === null) {
    return getErrorMessage(error, fallback);
  }
  const e = error as Record<string, unknown>;
  const ctx = e.context;
  let edgeMsg: unknown;
  if (typeof ctx === "object" && ctx !== null) {
    const c = ctx as Record<string, unknown>;
    const body = c.body;
    if (typeof body === "object" && body !== null) {
      const b = body as Record<string, unknown>;
      edgeMsg = b.error ?? b.message ?? body;
    } else if (typeof body === "string") {
      const t = body.trim();
      if (t.startsWith("{") || t.startsWith("[")) {
        try {
          const parsed = JSON.parse(t) as Record<string, unknown>;
          edgeMsg = parsed.error ?? parsed.message;
        } catch {
          edgeMsg = body;
        }
      } else {
        edgeMsg = body;
      }
    } else {
      edgeMsg = body;
    }
  }
  edgeMsg = edgeMsg ?? e.message;
  if (typeof edgeMsg === "string" && edgeMsg.trim().length > 0) return edgeMsg;
  return fallback;
}

/**
 * After `functions.invoke`, Supabase often returns `{ error }` alongside a parsed `data` body
 * when the Edge Function responded with JSON `{ "error": "..." }`. Prefer that message.
 */
export function invokeFailureMessage(data: unknown, error: unknown, fallback: string): string {
  if (data && typeof data === "object" && data !== null) {
    const d = data as Record<string, unknown>;
    const raw = d.error;
    if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
  }
  return getEdgeFunctionErrorMessage(error, fallback);
}

/**
 * `FunctionsHttpError` porte la `Response` brute dans `context` : son `body` est
 * un `ReadableStream`, jamais une string — les helpers synchrones retombaient
 * donc toujours sur le fallback et le vrai message (400 Stripe, 403…) n'arrivait
 * ni à l'utilisateur ni à Sentry. Cette variante lit le corps.
 */
export async function readEdgeFunctionError(
  error: unknown,
  fallback: string,
): Promise<{ message: string; status?: number; body?: string }> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (typeof Response !== "undefined" && ctx instanceof Response) {
    let body = "";
    try {
      body = await ctx.clone().text();
    } catch {
      /* corps déjà consommé */
    }
    let message = fallback;
    const trimmed = body.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        const raw = parsed.error ?? parsed.message;
        if (typeof raw === "string" && raw.trim().length > 0) message = raw.trim();
      } catch {
        if (trimmed.length > 0) message = trimmed;
      }
    } else if (trimmed.length > 0) {
      message = trimmed;
    }
    return { message, status: ctx.status, body: trimmed.slice(0, 500) };
  }
  return { message: getEdgeFunctionErrorMessage(error, fallback) };
}
